import { existsSync } from "node:fs";
import fs from "node:fs/promises";
import path from "node:path";
import { buildClaudePluginFiles } from "@skaile/workspaces/workspace-plugin";
import type { BackendStatus, InstallResult, ToggleResult, UninstallResult } from "./types.ts";

const SKAILE_MCP_NAME = "skaile-workspace";

const MCP_SERVER_ENTRY = {
  command: "skaile",
  args: ["mcp-server"],
  env: {} as Record<string, string>,
};

interface ClaudeSettings {
  plugins?: string[];
  enabledPlugins?: string[];
  disabledPlugins?: string[];
  disabledMcpjsonServers?: string[];
  [k: string]: unknown;
}

interface ClaudeMcpFile {
  mcpServers?: Record<string, unknown>;
  [k: string]: unknown;
}

/** Absolute path to the generated Claude Code plugin directory. */
function pluginDir(projectDir: string): string {
  return path.join(projectDir, ".claude", "plugins", "skaile");
}

async function readJsonStrict<T>(p: string): Promise<T | null> {
  let raw: string;
  try {
    raw = await fs.readFile(p, "utf-8");
  } catch {
    return null;
  }
  try {
    return JSON.parse(raw) as T;
  } catch (e: unknown) {
    throw new Error(
      `cannot parse ${p}: ${e instanceof Error ? e.message : String(e)}; please fix or remove`,
    );
  }
}

async function writeJson(p: string, value: unknown): Promise<void> {
  await fs.mkdir(path.dirname(p), { recursive: true });
  await fs.writeFile(p, `${JSON.stringify(value, null, 2)}\n`);
}

async function copyDir(src: string, dest: string): Promise<void> {
  await fs.mkdir(dest, { recursive: true });
  const entries = await fs.readdir(src, { withFileTypes: true });
  for (const entry of entries) {
    const srcPath = path.join(src, entry.name);
    const destPath = path.join(dest, entry.name);
    if (entry.isDirectory()) await copyDir(srcPath, destPath);
    else await fs.copyFile(srcPath, destPath);
  }
}

// ── install ────────────────────────────────────────────────────────────────

/**
 * Install the skaile integration for Claude Code.
 *
 * Two modes:
 * - **mcp** (default) — write `.claude/mcp.json` pointing at `skaile mcp-server`.
 * - **full** (`opts.full`) — generate the `.claude/plugins/skaile/` directory
 *   (MCP + lifecycle hooks) and register it in `.claude/settings.json`.
 */
export async function install(
  projectDir: string,
  opts?: { full?: boolean },
): Promise<InstallResult> {
  return opts?.full ? installFullPlugin(projectDir) : installMcp(projectDir);
}

async function installMcp(projectDir: string): Promise<InstallResult> {
  const mcpPath = path.join(projectDir, ".claude", "mcp.json");
  const existing = (await readJsonStrict<ClaudeMcpFile>(mcpPath)) ?? {};
  const mcpServers = { ...(existing.mcpServers ?? {}) };
  const previous = mcpServers[SKAILE_MCP_NAME];
  const matches = previous && JSON.stringify(previous) === JSON.stringify(MCP_SERVER_ENTRY);
  let warning: string | undefined;
  if (previous && !matches) {
    warning = `overwrote existing ${SKAILE_MCP_NAME} entry in ${path.relative(projectDir, mcpPath)}`;
  }
  mcpServers[SKAILE_MCP_NAME] = MCP_SERVER_ENTRY;
  await writeJson(mcpPath, { ...existing, mcpServers });
  return { changed: !matches, method: "mcp", warning };
}

async function installFullPlugin(projectDir: string): Promise<InstallResult> {
  const dir = pluginDir(projectDir);

  // Generate the plugin directory from the in-memory file map.
  for (const [rel, content] of Object.entries(buildClaudePluginFiles())) {
    const dest = path.join(dir, rel);
    await fs.mkdir(path.dirname(dest), { recursive: true });
    await fs.writeFile(dest, content);
  }

  // Populate skills/ from the project's .skaile/skills/.
  const projectSkillsDir = path.join(projectDir, ".skaile", "skills");
  if (existsSync(projectSkillsDir)) {
    await copyDir(projectSkillsDir, path.join(dir, "skills"));
  }

  // Register the generated dir in .claude/settings.json#plugins[].
  const settingsPath = path.join(projectDir, ".claude", "settings.json");
  const settings = (await readJsonStrict<ClaudeSettings>(settingsPath)) ?? {};
  const plugins = settings.plugins ?? [];
  if (!plugins.includes(dir)) plugins.push(dir);
  await writeJson(settingsPath, { ...settings, plugins });

  return { changed: true, method: "plugin" };
}

// ── uninstall ──────────────────────────────────────────────────────────────

export async function uninstall(projectDir: string): Promise<UninstallResult> {
  const settingsPath = path.join(projectDir, ".claude", "settings.json");
  const mcpPath = path.join(projectDir, ".claude", "mcp.json");
  const dir = pluginDir(projectDir);

  let changed = false;

  // Remove the generated plugin dir from plugins[] if registered.
  const settings = await readJsonStrict<ClaudeSettings>(settingsPath);
  if (settings && Array.isArray(settings.plugins)) {
    const next = settings.plugins.filter((p) => p !== dir);
    if (next.length !== settings.plugins.length) {
      changed = true;
      const updated: ClaudeSettings = { ...settings, plugins: next };
      if (next.length === 0) delete updated.plugins;
      await writeJson(settingsPath, updated);
    }
  }

  // Delete the generated plugin directory.
  if (existsSync(dir)) {
    await fs.rm(dir, { recursive: true, force: true });
    changed = true;
  }

  // Remove from .claude/mcp.json if present.
  const mcp = await readJsonStrict<ClaudeMcpFile>(mcpPath);
  if (mcp?.mcpServers && SKAILE_MCP_NAME in mcp.mcpServers) {
    changed = true;
    const { [SKAILE_MCP_NAME]: _removed, ...rest } = mcp.mcpServers;
    const updated: ClaudeMcpFile = { ...mcp, mcpServers: rest };
    if (Object.keys(rest).length === 0) delete updated.mcpServers;
    if (Object.keys(updated).length === 0) {
      await fs.unlink(mcpPath);
    } else {
      await writeJson(mcpPath, updated);
    }
  }

  return { changed };
}

// ── enable / disable ───────────────────────────────────────────────────────

async function detectInstall(
  projectDir: string,
): Promise<{ method: "plugin" | "mcp"; pluginPath?: string } | null> {
  const dir = pluginDir(projectDir);
  const settings = await readJsonStrict<ClaudeSettings>(
    path.join(projectDir, ".claude", "settings.json"),
  );
  if (
    existsSync(path.join(dir, ".claude-plugin", "plugin.json")) &&
    settings?.plugins?.includes(dir)
  ) {
    return { method: "plugin", pluginPath: dir };
  }

  const mcp = await readJsonStrict<ClaudeMcpFile>(path.join(projectDir, ".claude", "mcp.json"));
  if (mcp?.mcpServers && SKAILE_MCP_NAME in mcp.mcpServers) {
    return { method: "mcp" };
  }
  return null;
}

export async function enable(projectDir: string): Promise<ToggleResult> {
  const detected = await detectInstall(projectDir);
  if (!detected) throw new Error("plugin not installed for claude-code");

  const settingsPath = path.join(projectDir, ".claude", "settings.json");
  const settings = (await readJsonStrict<ClaudeSettings>(settingsPath)) ?? {};

  if (detected.method === "plugin") {
    const disabled = settings.disabledPlugins ?? [];
    const next = disabled.filter((p) => p !== detected.pluginPath);
    if (next.length === disabled.length) return { changed: false };
    const updated: ClaudeSettings = { ...settings, disabledPlugins: next };
    if (next.length === 0) delete updated.disabledPlugins;
    await writeJson(settingsPath, updated);
    return { changed: true };
  }

  const disabled = settings.disabledMcpjsonServers ?? [];
  const next = disabled.filter((n) => n !== SKAILE_MCP_NAME);
  if (next.length === disabled.length) return { changed: false };
  const updated: ClaudeSettings = { ...settings, disabledMcpjsonServers: next };
  if (next.length === 0) delete updated.disabledMcpjsonServers;
  await writeJson(settingsPath, updated);
  return { changed: true };
}

export async function disable(projectDir: string): Promise<ToggleResult> {
  const detected = await detectInstall(projectDir);
  if (!detected) throw new Error("plugin not installed for claude-code");

  const settingsPath = path.join(projectDir, ".claude", "settings.json");
  const settings = (await readJsonStrict<ClaudeSettings>(settingsPath)) ?? {};

  if (detected.method === "plugin") {
    const list = settings.disabledPlugins ?? [];
    if (list.includes(detected.pluginPath as string)) return { changed: false };
    await writeJson(settingsPath, {
      ...settings,
      disabledPlugins: [...list, detected.pluginPath as string],
    });
    return { changed: true };
  }

  const list = settings.disabledMcpjsonServers ?? [];
  if (list.includes(SKAILE_MCP_NAME)) return { changed: false };
  await writeJson(settingsPath, {
    ...settings,
    disabledMcpjsonServers: [...list, SKAILE_MCP_NAME],
  });
  return { changed: true };
}

// ── status ─────────────────────────────────────────────────────────────────

export async function status(projectDir: string): Promise<BackendStatus> {
  const settingsPath = path.join(projectDir, ".claude", "settings.json");
  const mcpPath = path.join(projectDir, ".claude", "mcp.json");
  const dir = pluginDir(projectDir);
  const settings = await readJsonStrict<ClaudeSettings>(settingsPath);
  const mcp = await readJsonStrict<ClaudeMcpFile>(mcpPath);

  const pluginInstalled = !!(
    existsSync(path.join(dir, ".claude-plugin", "plugin.json")) && settings?.plugins?.includes(dir)
  );
  const mcpInstalled = !!(mcp?.mcpServers && SKAILE_MCP_NAME in mcp.mcpServers);

  if (!pluginInstalled && !mcpInstalled) {
    return {
      backend: "claude-code",
      installed: "no",
      enabled: "n/a",
      method: "—",
      location: "—",
    };
  }

  if (pluginInstalled) {
    const disabled = settings?.disabledPlugins?.includes(dir) ?? false;
    return {
      backend: "claude-code",
      installed: "yes",
      enabled: disabled ? "no" : "yes",
      method: "plugin",
      location: path.relative(projectDir, dir),
    };
  }

  const disabled = settings?.disabledMcpjsonServers?.includes(SKAILE_MCP_NAME) ?? false;
  return {
    backend: "claude-code",
    installed: "yes",
    enabled: disabled ? "no" : "yes",
    method: "mcp",
    location: path.relative(projectDir, mcpPath),
  };
}
